import { supabase } from '@/core/database/supabase';
import { permissionEngine } from '@/core/permissions';
import type { StockOutPayload, InventoryStockOutItem } from '../types/inventoryStockOut.types';

export const inventoryStockOutService = {
  async getStockOutItem(id: string, companyId: string): Promise<InventoryStockOutItem> {
    const { data, error } = await supabase
      .from('inventory_items')
      .select('id, code, name, unit, current_stock, thumbnail_url')
      .eq('id', id)
      .eq('company_id', companyId)
      .single();

    if (error) throw error;
    return data as InventoryStockOutItem;
  },

  async createStockOut(payload: StockOutPayload) {
    await permissionEngine.assert(payload.created_by, 'inventory.transactions.create', { 
      companyId: payload.company_id 
    });

    // 1. Check stock availability
    const { data: item } = await supabase
      .from('inventory_items')
      .select('current_stock')
      .eq('id', payload.inventory_item_id)
      .single();

    if (!item || item.current_stock < payload.quantity) {
      throw new Error('S lng tồn kho khng   xut.');
    }

    // 2. Insert transaction
    let warehouseId = '00000000-0000-0000-0000-000000000000'; // Default
    const { data: wh } = await supabase
      .from('warehouses')
      .select('id')
      .eq('company_id', payload.company_id)
      .limit(1)
      .maybeSingle();
    
    if (wh) {
      warehouseId = wh.id;
    }

    // 2.5 Find a batch to deduct from (simple FIFO)
    const { data: batches } = await supabase
      .from('inventory_batches')
      .select('id, quantity_available')
      .eq('item_id', payload.inventory_item_id)
      .gt('quantity_available', 0)
      .order('received_date', { ascending: true })
      .limit(1);

    let batchId = null;
    if (batches && batches.length > 0) {
      batchId = batches[0].id;
      // Note: Full FIFO would split the transaction across multiple batches.
      // Here we just deduct from the first available for simplicity in MVP.
      const newBatchQty = Math.max(0, batches[0].quantity_available - payload.quantity);
      await supabase
        .from('inventory_batches')
        .update({ quantity_available: newBatchQty, quantity_on_hand: newBatchQty })
        .eq('id', batchId);
    }

    const { error: transError } = await supabase
      .from('inventory_transactions')
      .insert({
        item_id: payload.inventory_item_id,
        quantity: payload.quantity,
        transaction_date: payload.transaction_date,
        transaction_type: 'stock_out',
        note: payload.note,
        company_id: payload.company_id,
        created_by: payload.created_by,
        project_id: payload.project_id,
        work_order_id: payload.work_order_id,
        team_id: payload.team_id,
        warehouse_id: warehouseId,
        batch_id: batchId,
      });

    if (transError) throw transError;

    // 3. Update current stock
    const newStock = item.current_stock - payload.quantity;

    const { error: updateError } = await supabase
      .from('inventory_items')
      .update({ current_stock: newStock, updated_at: new Date().toISOString() })
      .eq('id', payload.inventory_item_id);

    if (updateError) throw updateError;
  },
};
